/**
 * Import Step 3: Review and Import Component
 * 
 * Third step of the 3-step import process
 * Shows preview of parsed transactions and import settings
 */

import React, { useState, useMemo } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { ParsedTransaction, ImportSettings } from '../../types/import.types';
import { transactionImportService } from '../../services/transaction-import-service';
import { parseTransactionRow } from '../../utils/import-column-mapper';
import type { ColumnMapping } from '../../types/import.types';
import { supabase } from '../../lib/supabase';

interface ImportStep3ReviewImportProps {
  csvRows: string[][];
  csvColumns: string[];
  mappings: ColumnMapping[];
  accountId: string;
  fileName?: string;
  fileSize?: number;
  onImportComplete: (result: { imported: number; failed: number; errors: string[]; warnings?: string[]; importSessionId?: string; totalRows?: number; skippedDuplicates?: number; skippedZeroAmounts?: number }) => void;
  onBack: () => void;
  onCancel: () => void;
}

export function ImportStep3ReviewImport({
  csvRows,
  csvColumns,
  mappings,
  accountId,
  fileName,
  fileSize,
  onImportComplete,
  onBack,
  onCancel
}: ImportStep3ReviewImportProps): React.ReactElement {
  const [settings, setSettings] = useState<ImportSettings>({
    skipDuplicates: true,
    autoCategorise: false,
    skipZeroAmounts: true,
    amountInvertSign: true // Default to Yes: Positive = debit, Negative = credit (liability behavior)
  });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{
    imported: number;
    failed: number;
    errors: string[];
    warnings?: string[];
  } | null>(null);

  // Helper to check if a row is a zero-amount transaction
  const isZeroAmountRow = (rowData: Record<string, string>, mappings: ColumnMapping[]): boolean => {
    const debitMapping = mappings.find(m => m.mappedField === 'debit');
    const creditMapping = mappings.find(m => m.mappedField === 'credit');
    const amountMapping = mappings.find(m => m.mappedField === 'amount');
    
    const cleanNumber = (value: string): number => {
      if (!value || value.trim() === '') return NaN;
      const cleaned = value.trim().replace(/[$,\s]/g, '');
      return parseFloat(cleaned);
    };

    // Check amount column if mapped
    if (amountMapping) {
      const amountRaw = (rowData[amountMapping.csvColumn] || '').trim();
      if (amountRaw !== '') {
        const amountValue = cleanNumber(amountRaw);
        return !isNaN(amountValue) && amountValue === 0;
      }
      return false;
    }
    
    // Check debit/credit columns
    const debitRaw = debitMapping ? (rowData[debitMapping.csvColumn] || '').trim() : '';
    const creditRaw = creditMapping ? (rowData[creditMapping.csvColumn] || '').trim() : '';
    
    const debitValue = debitRaw ? Math.abs(cleanNumber(debitRaw)) : 0;
    const creditValue = creditRaw ? Math.abs(cleanNumber(creditRaw)) : 0;
    
    // Check if either is explicitly zero
    return (debitRaw !== '' && !isNaN(debitValue) && debitValue === 0) ||
           (creditRaw !== '' && !isNaN(creditValue) && creditValue === 0) ||
           (debitRaw !== '' && creditRaw !== '' && debitValue === 0 && creditValue === 0);
  };

  // Parse transactions from CSV rows (reactive to settings changes)
  const { parsedTransactions, parseErrors, parseWarnings } = useMemo(() => {
    const parsed: ParsedTransaction[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    csvRows.slice(0, 50).forEach((row, index) => {
      const rowData: Record<string, string> = {};
      csvColumns.forEach((col, colIndex) => {
        rowData[col] = row[colIndex] || '';
      });

      try {
        const parsedRow = parseTransactionRow(rowData, mappings, settings.amountInvertSign ?? true);
        if (parsedRow) {
          parsed.push(parsedRow);
        } else {
          // Check if it's a zero-amount transaction
          if (isZeroAmountRow(rowData, mappings)) {
            const descriptionMapping = mappings.find(m => m.mappedField === 'description');
            const description = descriptionMapping ? (rowData[descriptionMapping.csvColumn] || '').trim() : '';
            warnings.push(`Row ${index + 2}: Zero amount transaction skipped${description ? ` (${description})` : ''}`);
          } else {
            errors.push(`Row ${index + 2}: Failed to parse transaction (missing required fields or invalid data)`);
          }
        }
      } catch (error: any) {
        errors.push(`Row ${index + 2}: ${error.message || 'Parse error'}`);
      }
    });

    return { parsedTransactions: parsed, parseErrors: errors, parseWarnings: warnings };
  }, [csvRows, csvColumns, mappings, settings.amountInvertSign]);

  const totalRows = csvRows.length;
  const previewCount = Math.min(50, totalRows);

  // Check if Amount column is mapped
  const hasAmountMapping = mappings.some(m => m.mappedField === 'amount');

  const handleImport = async () => {
    setImporting(true);
    setImportProgress(0);

    try {
      // Parse all transactions
      const allParsedTransactions: ParsedTransaction[] = [];
      const parseErrors: string[] = [];
      const parseWarnings: string[] = [];
      
      // Helper to check if a row is a zero-amount transaction
      const isZeroAmountRow = (rowData: Record<string, string>, mappings: ColumnMapping[]): boolean => {
        const debitMapping = mappings.find(m => m.mappedField === 'debit');
        const creditMapping = mappings.find(m => m.mappedField === 'credit');
        const amountMapping = mappings.find(m => m.mappedField === 'amount');
        
        const cleanNumber = (value: string): number => {
          if (!value || value.trim() === '') return NaN;
          const cleaned = value.trim().replace(/[$,\s]/g, '');
          return parseFloat(cleaned);
        };

        // Check amount column if mapped
        if (amountMapping) {
          const amountRaw = (rowData[amountMapping.csvColumn] || '').trim();
          if (amountRaw !== '') {
            const amountValue = cleanNumber(amountRaw);
            return !isNaN(amountValue) && amountValue === 0;
          }
          return false;
        }
        
        // Check debit/credit columns
        const debitRaw = debitMapping ? (rowData[debitMapping.csvColumn] || '').trim() : '';
        const creditRaw = creditMapping ? (rowData[creditMapping.csvColumn] || '').trim() : '';
        
        const debitValue = debitRaw ? Math.abs(cleanNumber(debitRaw)) : 0;
        const creditValue = creditRaw ? Math.abs(cleanNumber(creditRaw)) : 0;
        
        // Check if either is explicitly zero
        return (debitRaw !== '' && !isNaN(debitValue) && debitValue === 0) ||
               (creditRaw !== '' && !isNaN(creditValue) && creditValue === 0) ||
               (debitRaw !== '' && creditRaw !== '' && debitValue === 0 && creditValue === 0);
      };
      
      csvRows.forEach((row, index) => {
        const rowData: Record<string, string> = {};
        csvColumns.forEach((col, colIndex) => {
          rowData[col] = row[colIndex] || '';
        });

        try {
          const parsed = parseTransactionRow(rowData, mappings, settings.amountInvertSign ?? true);
          if (parsed) {
            allParsedTransactions.push(parsed);
          } else {
            // Check if it's a zero-amount transaction
            if (isZeroAmountRow(rowData, mappings)) {
              const descriptionMapping = mappings.find(m => m.mappedField === 'description');
              const description = descriptionMapping ? (rowData[descriptionMapping.csvColumn] || '').trim() : '';
              parseWarnings.push(`Row ${index + 2}: Zero amount transaction skipped${description ? ` (${description})` : ''}`);
            } else {
              parseErrors.push(`Row ${index + 2}: Failed to parse transaction (missing required fields or invalid data)`);
            }
          }
        } catch (error: any) {
          // Track parse errors instead of silently skipping
          parseErrors.push(`Row ${index + 2}: ${error.message || 'Parse error'}`);
        }
      });

      // Import transactions
      const result = await transactionImportService.importTransactions(
        {
          accountId,
          parsedTransactions: allParsedTransactions,
          columnMappings: mappings,
          settings,
          filename: fileName || 'import.csv',
          fileSize: fileSize || 1 // Default to 1 byte if not provided (minimum valid size)
        },
        (progress) => {
          setImportProgress(progress);
        }
      );

      if (result.error) {
        const importResult = {
          imported: 0,
          failed: allParsedTransactions.length,
          errors: [result.error.message],
          importSessionId: undefined,
          totalRows: csvRows.length,
          skippedDuplicates: 0,
          skippedZeroAmounts: 0
        };
        setImportResult(importResult);
        // Navigate to step 4 on error
        onImportComplete(importResult);
      } else if (result.data) {
        // Fetch detailed errors from import session if available
        let detailedErrors: string[] = [];
        if (result.data.failedRows > 0 && result.data.id) {
          try {
            const { data: session } = await supabase
              .from('import_sessions')
              .select('error_log')
              .eq('id', result.data.id)
              .single();
            
            if (session?.error_log) {
              // Parse error log (each line is an error)
              detailedErrors = session.error_log
                .split('\n')
                .filter(line => line.trim().length > 0);
            }
          } catch (err) {
            // If we can't fetch errors, show a generic message
            detailedErrors = [`${result.data.failedRows} transaction(s) failed to import`];
          }
        }

        // Combine parse errors with import errors (warnings are informational, not failures)
        const allErrors = [...parseErrors, ...detailedErrors];
        const allWarnings = [...parseWarnings];
        const totalFailed = parseErrors.length + result.data.failedRows;

        // Count skipped zero amounts from warnings
        const skippedZeroAmounts = allWarnings.filter(w => w.includes('Zero amount transaction skipped')).length;

        const importResult = {
          imported: result.data.importedRows,
          failed: totalFailed,
          errors: allErrors.length > 0 ? allErrors : (totalFailed > 0 ? [`${totalFailed} transaction(s) failed to import`] : []),
          warnings: allWarnings,
          importSessionId: result.data.id,
          totalRows: csvRows.length,
          skippedDuplicates: result.data.skippedDuplicates || 0,
          skippedZeroAmounts: skippedZeroAmounts
        };
        setImportResult(importResult);
        // Navigate to step 4 on completion
        onImportComplete(importResult);
      }
    } catch (error: any) {
      const importResult = {
        imported: 0,
        failed: 0,
        errors: [error.message || 'Import failed'],
        importSessionId: undefined,
        totalRows: csvRows.length,
        skippedDuplicates: 0,
        skippedZeroAmounts: 0
      };
      setImportResult(importResult);
      // Navigate to step 4 on error
      onImportComplete(importResult);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Settings */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Import Settings
        </h3>
        <div className="space-y-4">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={settings.skipDuplicates}
              onChange={(e) => setSettings({ ...settings, skipDuplicates: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Skip duplicate transactions
            </span>
          </label>
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={settings.skipZeroAmounts}
              onChange={(e) => setSettings({ ...settings, skipZeroAmounts: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Skip zero amount transactions
            </span>
          </label>

          {/* Amount Sign Interpretation Setting - Only show when Amount column is mapped */}
          {hasAmountMapping && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Treat positive amounts as debit (liability behavior)
              </label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="amountInvertSign"
                    checked={settings.amountInvertSign !== false}
                    onChange={() => setSettings({ ...settings, amountInvertSign: true })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Yes
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="amountInvertSign"
                    checked={settings.amountInvertSign === false}
                    onChange={() => setSettings({ ...settings, amountInvertSign: false })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    No
                  </span>
                </label>
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p>
                  <strong>Yes:</strong> Positive = debit (expense, increases debt), Negative = credit (payment, decreases debt) - for credit cards, loans
                </p>
                <p>
                  <strong>No:</strong> Positive = credit (money in), Negative = debit (money out) - for bank accounts, savings
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Preview ({previewCount} of {totalRows} transactions)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Type
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {parsedTransactions.slice(0, 10).map((transaction, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {transaction.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    ${transaction.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {transaction.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      transaction.type === 'debit'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    }`}>
                      {transaction.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {parsedTransactions.length > 10 && (
          <div className="p-4 bg-gray-50 dark:bg-gray-700 text-center text-sm text-gray-600 dark:text-gray-400">
            ... and {parsedTransactions.length - 10} more transactions
          </div>
        )}
      </div>

      {/* Parse Warnings */}
      {parseWarnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                Parse Warnings ({parseWarnings.length})
              </h4>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                {parseWarnings.slice(0, 5).map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
                {parseWarnings.length > 5 && (
                  <li>... and {parseWarnings.length - 5} more warnings</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Parse Errors */}
      {parseErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200 mb-1">
                Parse Errors ({parseErrors.length})
              </h4>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                {parseErrors.slice(0, 5).map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
                {parseErrors.length > 5 && (
                  <li>... and {parseErrors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Import Progress */}
      {importing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center space-x-4 mb-4">
            <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-1">
                Importing transactions...
              </h4>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                {importProgress}% complete
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Navigation Buttons */}
      {!importing && !importResult && (
        <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onBack}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
          >
            Back
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={parsedTransactions.length === 0}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              Import {totalRows} Transactions
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

